import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wine, Gem, User, Leaf, Sparkles, Mountain } from "lucide-react";
import { http, errMsg, EDITION_COLORS } from "../lib/api";
import { Input } from "../components/ui/input";
import { PdfPicker, uploadChunks } from "../components/kit/PdfImport";
import { ImportReview, reviewFrom, confirmBody } from "../components/kit/ImportReview";

const EDITIONS = [
  { k: "luxury", label: "Luxury", icon: Gem, desc: "Five-star, exclusive, private" },
  { k: "solo", label: "Solo", icon: User, desc: "Safe, easy, immersive for one" },
  { k: "wine", label: "Wine", icon: Wine, desc: "Cellar doors, tastings, pairings" },
  { k: "escape", label: "Escape", icon: Leaf, desc: "Wellness, nature, slow travel" },
  { k: "adventure", label: "Adventure", icon: Mountain, desc: "Treks, expeditions, lodges, safety" },
];
const TIERS = [
  { k: "mini-break", label: "Mini-Break", price: 219, founders: 279, days: 5 },
  { k: "premium", label: "Premium", price: 339, founders: 429, days: 9 },
  { k: "ultra", label: "Ultra", price: 469, founders: 599, days: 14 },
  { k: "elite", label: "Elite", price: 619, founders: 799, days: 19 },
];
const COLLECTIONS = [
  ["researched", "Researched Kit", "AI-assisted research, human-curated judgement · from $219"],
  ["founders", "Founder's Collection", "You tick the places you have been; written in your voice · from $279"],
  ["standalone", "Standalone Kit", "One destination, one topic — 10 profiles · $89 · PDF only"],
  ["guide", "One-off Guide", "A themed guide covering 4 subjects · $39 · PDF only"],
];
const TOPICS = [["accommodation", "Accommodation"], ["destination", "Destination"], ["dining", "Dining"], ["experiences", "Experiences"]];
const SEASONS = ["Spring", "Summer", "Autumn", "Winter"];

const Choice = ({ active, onClick, children, id, color }) => (
  <button type="button" data-testid={id} onClick={onClick}
    className={`text-left rounded-xl border p-4 transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 ${active ? "bg-stone-100" : "border-stone-200 bg-white hover:border-stone-300"}`}
    style={active ? { borderColor: color } : undefined}>
    {children}
  </button>
);

export default function NewKit() {
  const nav = useNavigate();
  const [f, setF] = useState({ collection: "researched", edition: "wine", destination: "", season: SEASONS[1], tier: "mini-break", days: 5, topic: "accommodation", subjects: ["", "", "", ""] });
  const [model, setModel] = useState("");
  useEffect(() => { http.get("/options").then((r) => setModel(r.data.default_model)).catch(() => {}); }, []);
  const full = ["researched", "founders"].includes(f.collection);
  const guide = f.collection === "guide";
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState("research");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const importing = source === "pdf";
  const [review, setReview] = useState(null);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (importing && !file) return toast.error("Choose your PDF or HTML file first");
    setBusy(true);
    try {
      if (importing && review) {
        const { data } = await http.post(`/imports/${review.iid}/confirm`, confirmBody(review));
        toast.success(`Converting ${review.destination}`);
        return nav(`/kits/${data.id}`);
      }
      if (importing) {
        setProgress(0);
        const iid = await uploadChunks(file, { bulk: true }, setProgress);
        const { data: g } = await http.post(`/imports/${iid}/detect`);
        setReview({ ...reviewFrom(g, iid, f.collection), ...(f.collection === "standalone" ? { topic: f.topic } : {}) });
        setBusy(false);
        setProgress(null);
        return;
      }
      const { data } = await http.post("/kits", { ...f, days: full ? Number(f.days) : 0 });
      toast.success(f.collection === "founders" ? "Finding venue options for you to verify" : "Research started — this takes 6–15 minutes");
      nav(`/kits/${data.id}`);
    } catch (err) {
      toast.error(err.response ? errMsg(err) : err.message);
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <form onSubmit={submit} className="p-8 lg:p-12 max-w-5xl" data-testid="new-kit-form">
      <div className="eyebrow rise">New Brief</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2B2A26] mt-2 mb-12 rise">Commission a kit</h1>

      <div className="eyebrow mb-3">Collection</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
        {COLLECTIONS.map(([k, l, d]) => (
          <Choice key={k} id={`collection-option-${k}`} active={f.collection === k} onClick={() => set("collection", k)} color="#C9A847">
            <div className="font-display text-xl text-[#2B2A26]">{l}</div><div className="text-xs text-stone-500 mt-1">{d}</div>
          </Choice>
        ))}
      </div>

      {(<>
        <div className="eyebrow mb-3">Start from</div>
        <div className="grid grid-cols-2 gap-3 mb-6 max-w-2xl">
          <Choice id="source-option-research" active={source === "research"} onClick={() => setSource("research")} color="#C9A847">
            <div className="font-display text-lg text-[#2B2A26]">{f.collection === "founders" ? "Pick my places" : "Fresh research"}</div><div className="text-xs text-stone-500 mt-1">{f.collection === "founders" ? "Tick the places you've been, then AI writes it" : "AI researches and writes the whole kit"}</div>
          </Choice>
          <Choice id="source-option-pdf" active={source === "pdf"} onClick={() => setSource("pdf")} color="#C9A847">
            <div className="font-display text-lg text-[#2B2A26]">Import my PDF / HTML</div><div className="text-xs text-stone-500 mt-1">Convert an existing {COLLECTIONS.find((c) => c[0] === f.collection)[1]} into a verified kit</div>
          </Choice>
        </div>
        {importing && review ? <ImportReview r={review} setR={setReview} filename={file?.name} />
          : importing ? <PdfPicker file={file} setFile={(x) => { setFile(x); setReview(null); }} progress={progress} /> : <div className="mb-4" />}
        {importing && !review && f.collection === "standalone" && (<>
          <div className="eyebrow mb-3">Topic of this guide</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
            {TOPICS.map(([k, l]) => (
              <Choice key={k} id={`import-topic-option-${k}`} active={f.topic === k} onClick={() => set("topic", k)} color="#C9A847">
                <div className="font-display text-xl text-[#2B2A26]">{l}</div>
              </Choice>
            ))}
          </div>
        </>)}
        {importing && !review && f.collection === "founders" && <p data-testid="import-founders-note" className="text-xs text-stone-500 -mt-6 mb-10 max-w-2xl">After upload you can untick any venue you haven't visited before the import starts.</p>}
        {importing && !review && f.collection === "guide" && <p data-testid="import-guide-note" className="text-xs text-stone-500 -mt-6 mb-10 max-w-2xl">The guide title and its 4 subjects are read from your file.</p>}
      </>)}

      {!importing && (<>
      <div className="eyebrow mb-3">01 · Edition</div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-10">
        {EDITIONS.map(({ k, label, icon: Icon, desc }) => (
          <Choice key={k} id={`edition-option-${k}`} active={f.edition === k} onClick={() => set("edition", k)} color={EDITION_COLORS[k].border}>
            <Icon size={20} style={{ color: EDITION_COLORS[k].border }} />
            <div className="font-display text-xl mt-3 text-[#2B2A26]">{label}</div>
            <div className="text-xs text-stone-500 mt-1">{desc}</div>
          </Choice>
        ))}
      </div>

      {f.collection === "standalone" && (<>
        <div className="eyebrow mb-3">Topic</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          {TOPICS.map(([k, l]) => (
            <Choice key={k} id={`topic-option-${k}`} active={f.topic === k} onClick={() => set("topic", k)} color="#C9A847">
              <div className="font-display text-xl text-[#2B2A26]">{l}</div><div className="font-mono-ui text-sm text-[#9A7B2C] mt-1">$89 AUD</div>
            </Choice>
          ))}
        </div>
      </>)}

      <div className="grid md:grid-cols-2 gap-6 mb-10">
        <div>
          <div className="eyebrow mb-3">02 · {guide ? "Guide title" : "Destination"}</div>
          <Input data-testid="destination-input" required minLength={2} placeholder={guide ? "e.g. Cruise & Rail Pre-Trip Kits" : "e.g. Kyoto, Japan"} value={f.destination} onChange={(e) => set("destination", e.target.value)} className="h-12 bg-white text-base" />
        </div>
        {full && <div>
          <div className="eyebrow mb-3">03 · Season</div>
          <Input data-testid="season-input" list="seasons" required value={f.season} onChange={(e) => set("season", e.target.value)} className="h-12 bg-white text-base" />
          <datalist id="seasons">{SEASONS.map((s) => <option key={s} value={s} />)}</datalist>
        </div>}
      </div>

      {guide && (<>
        <div className="eyebrow mb-3">03 · The four subjects</div>
        <div className="grid md:grid-cols-2 gap-3 mb-12 max-w-3xl">
          {f.subjects.map((v, i) => (
            <Input key={i} data-testid={`subject-input-${i}`} required placeholder={["The Ghan", "Rocky Mountaineer", "The Douro", "Small-Ship Cruising"][i]} value={v}
              onChange={(e) => setF((s) => ({ ...s, subjects: s.subjects.map((x, j) => (j === i ? e.target.value : x)) }))} className="h-12 bg-white text-base" />
          ))}
        </div>
      </>)}

      {full && (<>
      <div className="eyebrow mb-3">04 · Tier</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
        {TIERS.map((t) => (
          <Choice key={t.k} id={`tier-option-${t.k}`} active={f.tier === t.k} onClick={() => setF((s) => ({ ...s, tier: t.k, days: t.days }))} color="#C9A847">
            <div className="font-display text-xl text-[#2B2A26]">{t.label}</div>
            <div className="font-mono-ui text-sm text-[#9A7B2C] mt-1">${f.collection === "founders" ? t.founders : t.price} AUD</div>
          </Choice>
        ))}
      </div>

      <div className="eyebrow mb-3">05 · Number of days</div>
      <Input data-testid="days-input" type="number" min={2} max={30} required value={f.days} onChange={(e) => set("days", e.target.value)} className="h-12 w-40 bg-white text-base mb-12" />
      </>)}
      </>)}

      <div className="eyebrow mb-3">AI writer</div>
      <div data-testid="ai-writer-model" className="mb-12 max-w-xl rounded-sm border border-stone-200 bg-white px-5 py-4">
        <div className="font-display text-lg text-[#2B2A26]">{model || "…"}</div>
        <div className="text-xs text-stone-500 mt-1">Every kit is written and web-researched through OpenRouter with this model.</div>
      </div>

      <button data-testid="generate-kit-button" disabled={busy} className="inline-flex items-center gap-2 rounded-sm bg-[#C9A847] text-white px-8 py-4 font-bold hover:bg-[#B8973A] transition-colors disabled:opacity-60">
        <Sparkles size={18} /> {importing ? (busy ? (review ? "Starting import…" : progress === 100 ? "Reading your file…" : "Uploading…") : review ? "Start import" : "Upload & check") : busy ? "Starting research…" : "Research & build kit"}
      </button>
      {review && <button type="button" data-testid="review-choose-other-file" onClick={() => { setReview(null); setFile(null); }} className="ml-4 text-sm text-stone-500 underline hover:text-stone-900">Choose a different file</button>}
      <p className="text-xs text-stone-500 mt-4 max-w-lg">The AI writer drafts every section, then every venue website is checked live. Anything doubtful is flagged for your review before publishing.</p>
    </form>
  );
}
